import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet, TrendingDown, Leaf, Zap, Star, Download, FileText, Award,
  AlertTriangle, ListChecks, Coins, Banknote, ClipboardList, Sparkles, RefreshCw,
  TreePine, Car,
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, API, fmtEur, fmtNum, downloadReportPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Report() {
  const { leadId } = useParams();
  const { admin } = useAuth();
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchReport = useCallback(() => api.get(`/leads/${leadId}/report${admin ? "" : "?track=1"}`).then((r) => setLead(r.data)).catch(() => setErr(true)), [leadId, admin]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const updateReport = async () => {
    if (!window.confirm("Aggiornare la relazione con i dati modificati e le regole/valutazioni più recenti? Verrà rigenerato il dossier consulenziale.")) return;
    setUpdating(true);
    try {
      await api.post(`/admin/leads/${leadId}/recalculate`);
      toast.info("Aggiornamento della relazione in corso…");
      let st = "processing";
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = await api.get(`/leads/${leadId}/status`);
        st = s.data.status;
        if (st !== "processing") break;
      }
      await fetchReport();
      toast[st === "error" ? "error" : "success"](st === "error" ? "Aggiornamento fallito" : "Relazione aggiornata con i dati più recenti");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito");
    } finally {
      setUpdating(false);
    }
  };

  if (err) return <Centered>Report non disponibile.</Centered>;
  if (!lead) return <Centered>Caricamento del dossier…</Centered>;

  const a = lead.analysis || {};
  const eoi = a.eoi || {};
  const twin = a.digital_twin || [];
  const r = a.report || {};
  const comm = lead.commercial || {};

  const chartData = twin.filter((s) => s.annual_saving_eur).map((s) => ({
    name: s.name, saving: Math.round(s.annual_saving_eur), recommended: s.recommended,
  }));

  const downloadPdf = async () => {
    const name = `Audit_${(lead.company?.ragione_sociale || "azienda").replace(/\s+/g, "_")}.pdf`;
    try {
      await downloadReportPdf(leadId, name);
    } catch {
      window.open(`${API}/leads/${leadId}/report/pdf`, "_blank");
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            {admin && (
              <Button data-testid="update-report-btn" onClick={updateReport} disabled={updating}
                className="h-10 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white font-semibold">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${updating ? "animate-spin" : ""}`} /> {updating ? "Aggiornamento…" : "Aggiorna relazione"}
              </Button>
            )}
            <Button data-testid="download-pdf-btn" onClick={downloadPdf}
              className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold">
              <Download className="h-4 w-4 mr-1.5" /> Scarica PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs text-gold-300 mb-3">
            <Award className="h-3.5 w-3.5" /> Dossier consulenziale · {lead.company?.ragione_sociale}
          </div>
          <h1 className="font-heading font-black text-3xl lg:text-4xl text-white tracking-tight">
            Audit Energetico Professionale
          </h1>
          {lead.company?.ragione_sociale && (
            <p className="mt-2 text-xl lg:text-2xl font-heading font-bold text-gold-300" data-testid="report-company-name">
              {lead.company.ragione_sociale}
            </p>
          )}
        </motion.div>

        {/* EOI hero */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mt-8 rounded-3xl border border-gold-500/25 bg-gradient-to-br from-gold-500/[0.09] via-transparent to-transparent p-8 lg:p-10 glow-gold relative overflow-hidden">
          <div className="grid-lines absolute inset-0 opacity-20" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-gold-400" />
              <h2 className="font-heading font-bold text-xl text-white">Energy Opportunity Index</h2>
              <span className="ml-auto flex items-center gap-1.5 rounded-full bg-gold-500/15 px-3 py-1 text-sm text-gold-300">
                <Star className="h-4 w-4" fill="currentColor" /> Priorità {fmtNum(eoi.overall_priority, 1)}/10
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <EoiCard icon={Wallet} label="Opportunità economica (10 anni)" value={fmtEur(eoi.economic_opportunity_10y_eur)} big />
              <EoiCard icon={TrendingDown} label="Riduzione costi energetici" value={`${fmtNum(eoi.cost_reduction_pct)}%`} />
              <EoiCard icon={Leaf} label="Riduzione CO₂" value={`${fmtNum(eoi.co2_reduction_tons)} t`} />
              <EoiCard icon={Zap} label="Autonomia energetica" value={`${fmtNum(eoi.energy_autonomy_pct)}%`} />
            </div>
          </div>
        </motion.div>

        {/* Commercial proposal */}
        {(comm.energy_supplier || comm.gas_supplier || comm.pv_turnkey || comm.pv_seu) && (
          <section className="mt-10">
            <h2 className="font-heading font-bold text-xl text-white mb-1">La tua transizione energetica · Proposta DM Solutions</h2>
            <p className="text-slate-400 text-sm mb-1">L'obiettivo dell'audit è portarti a produrre e controllare la tua energia: il fotovoltaico è la leva strategica per abbattere i costi e proteggerti dai rincari.</p>
            <p className="text-slate-500 text-xs mb-6" data-testid="vat-note">Tutti i prezzi, le tariffe e le valutazioni economiche sono espressi <b className="text-slate-400">al netto dell'IVA</b> (forniture per aziende).</p>

            {comm.scenarios && (comm.scenarios.standard || comm.scenarios.premium) && (
              <div className="mb-10" data-testid="scenarios-section">
                <h3 className="font-heading font-bold text-lg text-gold-300 mb-1">I tuoi 3 percorsi</h3>
                <p className="text-slate-400 text-sm mb-5">Tre livelli di intervento a confronto, dal più immediato al più completo. Scegli il ritmo della tua transizione energetica.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[comm.scenarios.light, comm.scenarios.standard, comm.scenarios.premium].filter(Boolean).map((sc) => {
                    const featured = sc.id === "standard";
                    return (
                      <div key={sc.id} data-testid={`scenario-${sc.id}`}
                        className={`rounded-2xl p-6 flex flex-col transition-all duration-300 ${featured ? "border border-gold-500/50 bg-gold-500/[0.06] md:-translate-y-2 glow-gold" : "card-dark"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-500">{sc.id === "light" ? "Livello 1" : sc.id === "standard" ? "Livello 2" : "Livello 3"}</span>
                          {featured && <span className="text-[10px] uppercase tracking-wider text-[#0A1A2F] bg-gold-500 px-2 py-0.5 rounded-full font-bold">Consigliato</span>}
                        </div>
                        <h4 className="font-heading font-bold text-white text-lg leading-tight">{sc.titolo}</h4>
                        <p className="text-xs text-slate-400 mt-1 mb-4">{sc.sottotitolo}</p>
                        <div className="mb-4">
                          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Risparmio netto / anno</div>
                          <div className="font-heading font-black text-2xl text-gold-400">{fmtEur(sc.risparmio_netto_anno)}</div>
                        </div>
                        <div className="space-y-2 text-sm border-t border-white/10 pt-3">
                          <Row k="Investimento" v={sc.investimento ? fmtEur(sc.investimento) : "Nessuno"} />
                          {sc.roi_anni ? <Row k="Rientro (ROI)" v={`${fmtNum(sc.roi_anni, 1)} anni`} /> : null}
                          {sc.kwp ? <Row k="Potenza FV" v={`${fmtNum(sc.kwp, 1)} kWp`} /> : null}
                          {sc.id === "premium" && sc.accumulo_kwh ? <Row k="Accumulo" v={`${fmtNum(sc.accumulo_kwh)} kWh`} /> : null}
                          {sc.id === "premium" && sc.autonomia_pct != null ? <Row k="Autonomia energetica" v={`${fmtNum(sc.autonomia_pct)}%`} /> : null}
                          {sc.id === "standard" && sc.autoconsumo_pct != null ? <Row k="Autoconsumo" v={`${fmtNum(sc.autoconsumo_pct)}%`} /> : null}
                          {sc.co2_ton_anno ? <Row k="CO2 evitata/anno" v={`${fmtNum(sc.co2_ton_anno, 1)} t`} /> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(comm.pv_seu || comm.pv_turnkey) && (
              <>
                <h3 className="font-heading font-bold text-lg text-gold-300 mb-4">Soluzione fotovoltaica consigliata</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {comm.pv_seu && (
                    <div data-testid="pv-seu-card" className="rounded-2xl p-6 border border-gold-500/50 bg-gold-500/[0.06] glow-gold">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-gold-300">Fotovoltaico · Opzione B</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#0A1A2F] bg-gold-500 px-2 py-0.5 rounded-full font-bold">Consigliata</span>
                      </div>
                      <h3 className="font-heading font-bold text-white mt-1 mb-2">Investimento DM + SEU · zero capitale</h3>
                      <p className="text-xs text-slate-300 mb-4 leading-snug">
                        Nessun investimento a tuo carico: DM realizza l'impianto con cessione del diritto di superficie per {fmtNum(comm.pv_seu.diritto_superficie_anni)} anni e ti vende energia a prezzo fisso SEU inferiore all'attuale. Ottieni subito energia più economica, indipendenza dai rincari e un'immagine green, senza immobilizzare capitale. <b className="text-slate-200">Sei mesi prima della scadenza</b> scegli tra proroga di altri {fmtNum(comm.pv_seu.anni_aggiuntivi)} anni alle stesse condizioni oppure riscatto dell'impianto al {fmtNum(comm.pv_seu.riscatto_pct)}% del valore iniziale ({fmtEur(comm.pv_seu.riscatto_valore)}).
                      </p>
                      <div className="space-y-2 text-sm">
                        <Row k="Investimento cliente" v={fmtEur(comm.pv_seu.investimento_cliente)} gold />
                        <Row k="Produzione annua impianto" v={`${fmtNum(comm.pv_seu.produzione_kwh)} kWh`} />
                        <Row k="Energia venduta al cliente a tariffa SEU" v={`${fmtNum(comm.pv_seu.energia_seu_kwh || comm.pv_seu.autoconsumo_kwh)} kWh/anno`} />
                        {comm.pv_seu.surplus_kwh > 0 && (
                          <Row k="Eccedenza immessa in rete (gestita da DM)" v={`${fmtNum(comm.pv_seu.surplus_kwh)} kWh/anno`} />
                        )}
                        <Row k="Prezzo finito attuale (escl. IVA)" v={`${fmtNum(comm.pv_seu.prezzo_attuale, 3)} €/kWh`} />
                        <Row k={`Prezzo SEU (−${fmtNum(comm.pv_seu.sconto_pct)}%)`} v={`${fmtNum(comm.pv_seu.prezzo_seu, 3)} €/kWh`} gold />
                        <Row k="Risparmio/anno" v={fmtEur(comm.pv_seu.risparmio_anno)} gold />
                        <Row k={`Risparmio ${fmtNum(comm.pv_seu.durata_anni)} anni`} v={fmtEur(comm.pv_seu.risparmio_totale)} gold />
                        <Row k={`Riscatto impianto a fine contratto (${fmtNum(comm.pv_seu.riscatto_pct)}% valore iniziale)`} v={fmtEur(comm.pv_seu.riscatto_valore)} />
                      </div>
                      {comm.pv_seu.ripartizione_fasce && <BandSplitBar rip={comm.pv_seu.ripartizione_fasce} />}
                    </div>
                  )}
                  {comm.pv_turnkey && (
                    <div data-testid="pv-turnkey-card" className={`card-dark rounded-2xl p-6 ${!comm.pv_seu ? "border border-gold-500/50 bg-gold-500/[0.06] glow-gold" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">Fotovoltaico · Opzione A</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-300 bg-white/10 px-2 py-0.5 rounded-full">{comm.pv_seu ? "Alternativa · proprietà immediata" : "Consigliata"}</span>
                      </div>
                      <h3 className="font-heading font-bold text-white mt-1 mb-4">Acquisto chiavi in mano{comm.pv_turnkey.tipo_installazione === "terra" ? " · impianto a terra (agrivoltaico)" : ""}</h3>
                      <div className="space-y-2 text-sm">
                        <Row k="Potenza consigliata" v={`${fmtNum(comm.pv_turnkey.kwp, 1)} kWp`} />
                        <Row k="Produzione annua" v={`${fmtNum(comm.pv_turnkey.produzione_kwh)} kWh`} />
                        <Row k={`Autoconsumo (${fmtNum(comm.pv_turnkey.autoconsumo_pct)}%)`} v={`${fmtNum(comm.pv_turnkey.autoconsumo_kwh)} kWh`} />
                        <Row k={`Surplus venduto a mercato (${fmtNum(comm.pv_turnkey.surplus_prezzo_eur_kwh, 2)} €/kWh)`} v={`${fmtNum(comm.pv_turnkey.surplus_kwh)} kWh · ${fmtEur(comm.pv_turnkey.ricavo_surplus_eur)}`} />
                        {comm.pv_turnkey.battery_kw > 0 && (
                          <Row k={`Accumulo incluso (batterie ${fmtNum(comm.pv_turnkey.battery_units)}×5 kW)`} v={`${fmtNum(comm.pv_turnkey.battery_kw)} kWh · ${fmtEur(comm.pv_turnkey.battery_investimento)}`} />
                        )}
                        <Row k="Investimento (chiavi in mano)" v={fmtEur(comm.pv_turnkey.investimento)} />
                        <Row k="Risparmio lordo/anno (autoconsumo + surplus)" v={fmtEur(comm.pv_turnkey.risparmio_anno)} />
                        <Row k={`Costi gestione/anno (manut. + pulizia + assicur.) — indicativo *`} v={`− ${fmtEur(comm.pv_turnkey.costo_gestione_anno)}`} />
                        <Row k="Risparmio netto/anno" v={fmtEur(comm.pv_turnkey.risparmio_netto_anno)} gold />
                        <Row k="Rientro (ROI)" v={`${fmtNum(comm.pv_turnkey.roi_anni, 1)} anni`} />
                      </div>
                      {comm.pv_turnkey.ripartizione_fasce && <BandSplitBar rip={comm.pv_turnkey.ripartizione_fasce} surplusLabel="Surplus venduto a mercato" />}
                      <p className="text-[11px] text-slate-500 mt-3" data-testid="om-cost-disclaimer"><b className="text-slate-400">*</b> Il costo di gestione indicato (manutenzione ordinaria, pulizia annua e assicurazione dell'impianto) è <b className="text-slate-400">indicativo</b>: non costituisce un prezzo confermato. Il valore effettivo sarà definito solo con un <b className="text-slate-400">preventivo dedicato</b> e successivo contratto di servizio.</p>
                      {comm.pv_turnkey.listino_separato && (
                        <p className="text-[11px] text-amber-300/80 mt-2">Impianto sotto i 50 kW: prezzo indicativo, soggetto a listino dedicato.</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {(comm.energy_supplier || comm.gas_supplier) && (
              <div className="card-dark rounded-2xl p-6 mt-6">
                <h3 className="font-heading font-bold text-white mb-1">Ottimizzazione della fornitura — Offerta CTE <span className="text-slate-500 text-xs font-normal">(complementare)</span></h3>
                <p className="text-slate-500 text-xs mb-4">A completamento dell'impianto fotovoltaico, ottimizziamo la fornitura sull'energia <b>residua</b>. Confronto sulla sola componente <b>materia energia/gas</b> (esclusi trasporto, oneri, accise e IVA).</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-500 border-b border-white/10">
                      <th className="py-2 pr-4">Vettore</th><th className="py-2 pr-4">Consumo</th>
                      <th className="py-2 pr-4">Prezzo materia attuale</th><th className="py-2 pr-4">Prezzo DM (CTE)</th>
                      <th className="py-2">Risparmio/anno</th></tr></thead>
                    <tbody>
                      {comm.energy_supplier && (
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 pr-4 text-slate-200">Energia elettrica</td>
                          <td className="py-2.5 pr-4 font-mono-plex text-slate-300">
                            {fmtNum(comm.energy_supplier.consumo_fornitura_kwh ?? comm.energy_supplier.consumo_kwh)} kWh
                            {comm.energy_supplier.autoconsumo_fv_kwh > 0 && (
                              <span className="block text-[10px] text-slate-500">di fornitura (escl. autoconsumo FV {fmtNum(comm.energy_supplier.autoconsumo_fv_kwh)} kWh)</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4 font-mono-plex text-slate-300">{fmtNum(comm.energy_supplier.prezzo_attuale, 3)} €/kWh</td>
                          <td className="py-2.5 pr-4 font-mono-plex text-gold-400">{fmtNum(comm.energy_supplier.prezzo_dm, 3)} €/kWh</td>
                          <td className="py-2.5 font-mono-plex text-gold-400">{fmtEur(comm.energy_supplier.risparmio_anno)}</td>
                        </tr>
                      )}
                      {comm.gas_supplier && (
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 pr-4 text-slate-200">Gas naturale</td>
                          <td className="py-2.5 pr-4 font-mono-plex text-slate-300">{fmtNum(comm.gas_supplier.consumo_smc)} Smc</td>
                          <td className="py-2.5 pr-4 font-mono-plex text-slate-300">{fmtNum(comm.gas_supplier.prezzo_attuale, 3)} €/Smc</td>
                          <td className="py-2.5 pr-4 font-mono-plex text-gold-400">{fmtNum(comm.gas_supplier.prezzo_dm, 3)} €/Smc</td>
                          <td className="py-2.5 font-mono-plex text-gold-400">{fmtEur(comm.gas_supplier.risparmio_anno)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}


        {/* Digital twin scenarios */}
        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl text-white mb-1">Gemello Digitale · Scenari di intervento</h2>
          <p className="text-slate-400 text-sm mb-6">Simulazioni economiche per ciascuno scenario modellato sull'azienda.</p>

          {chartData.length > 0 && (
            <div className="card-dark rounded-2xl p-6 mb-6">
              <p className="text-sm text-slate-300 mb-4">Risparmio annuo stimato per scenario</p>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer minWidth={0}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={60} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }}
                      contentStyle={{ background: "#0F2440", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                      formatter={(v) => [fmtEur(v), "Risparmio/anno"]} />
                    <Bar dataKey="saving" radius={[6, 6, 0, 0]}>
                      {chartData.map((d, i) => <Cell key={i} fill={d.recommended ? "#D4A73A" : "#2A4667"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {twin.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.04 * i }}
                className={`rounded-2xl p-5 border transition-colors ${s.recommended ? "border-gold-500/40 bg-gold-500/[0.05]" : "card-dark"}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-bold text-white">{s.name}</h3>
                  {s.recommended && <span className="text-[10px] uppercase tracking-wider text-gold-300 bg-gold-500/15 px-2 py-0.5 rounded-full">Consigliato</span>}
                </div>
                <p className="text-slate-400 text-xs leading-snug mb-4 min-h-[32px]">{s.description}</p>
                <div className="grid grid-cols-2 gap-y-2.5 text-sm">
                  <Stat label="Investimento" value={fmtEur(s.investment_eur)} />
                  <Stat label="Risparmio/anno" value={fmtEur(s.annual_saving_eur)} good />
                  <Stat label="ROI" value={`${fmtNum(s.roi_years, 1)} anni`} />
                  <Stat label="CO₂" value={`${fmtNum(s.co2_reduction_tons)} t`} />
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Report sections */}
        <section className="mt-10 space-y-6">
          <Section icon={FileText} title="Executive Summary">{r.executive_summary}</Section>
          <Section icon={FileText} title="Analisi delle bollette">{r.bill_analysis}</Section>
          <Section icon={Award} title="Benchmark di settore">{r.benchmark}</Section>

          <ListSection icon={AlertTriangle} title="Criticità individuate" items={r.criticalities} tone="amber" />

          {r.recommended_interventions?.length > 0 && (
            <div className="card-dark rounded-2xl p-6">
              <SectionHead icon={ListChecks} title="Interventi consigliati · per priorità" />
              <div className="space-y-3 mt-4">
                {r.recommended_interventions.map((it, i) => (
                  <div key={i} className="flex gap-4 rounded-xl bg-white/[0.02] border border-white/5 p-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold-500/15 text-gold-300 font-bold text-sm">{it.priority || i + 1}</div>
                    <div>
                      <p className="text-white font-medium text-sm">{it.title}</p>
                      <p className="text-slate-400 text-sm mt-1 leading-snug">{it.description}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                        {it.payback_years != null && <span>⏱ Payback: {fmtNum(it.payback_years, 1)} anni</span>}
                        {it.incentive && <span>🎁 {it.incentive}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Section icon={Coins} title="Simulazione economica">{r.economic_simulation}</Section>
          <ListSection icon={Banknote} title="Incentivi disponibili" items={r.incentives} tone="gold" />
          {(r.co2_reduction_summary || eoi.co2_reduction_tons > 0) && (
            <div className="card-dark rounded-2xl p-6" data-testid="co2-section">
              <SectionHead icon={Leaf} title="Riduzione delle emissioni" />
              {r.co2_reduction_summary && <p className="text-slate-300 text-sm leading-relaxed mt-3">{r.co2_reduction_summary}</p>}
              {eoi.co2_reduction_tons > 0 && <Co2Equivalents tons={eoi.co2_reduction_tons} />}
            </div>
          )}
          <ListSection icon={ClipboardList} title="Piano operativo" items={r.operational_plan} tone="blue" ordered />
          <ListSection icon={Banknote} title="Modalità di finanziamento" items={r.financing_options} tone="gold" />
        </section>

        <div className="mt-10 rounded-2xl border border-white/10 p-6 text-center">
          <p className="text-slate-300 text-sm">
            La progettazione definitiva, la validazione tecnica e la proposta commerciale sono a cura del team di
            <b className="text-white"> DM Solutions Group</b>. Un consulente ti contatterà per approfondire.
          </p>
          <Button data-testid="download-pdf-btn-2" onClick={downloadPdf}
            className="mt-4 h-11 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold">
            <Download className="h-4 w-4 mr-1.5" /> Scarica il dossier PDF
          </Button>
        </div>

        <p className="mt-6 text-center text-[12px] text-slate-500 leading-relaxed max-w-3xl mx-auto" data-testid="consulenza-clause">
          Per la gestione delle forniture di energia e gas, oltre alle successive valutazioni e modifiche di prodotto,
          all'audit predittivo e alle ulteriori soluzioni offerte, è necessario sottoscrivere il contratto di consulenza con la nostra società.
        </p>
      </main>
    </div>
  );
}

const Row = ({ k, v, gold }) => (
  <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-1.5">
    <span className="text-slate-400 leading-snug">{k}</span>
    <span className={`font-mono-plex whitespace-nowrap text-right shrink-0 ${gold ? "text-gold-400" : "text-slate-200"}`}>{v}</span>
  </div>
);

const Co2Equivalents = ({ tons }) => {
  const kg = (Number(tons) || 0) * 1000;
  const trees = Math.round(kg / 22);
  const km = Math.round(kg / 0.12);
  return (
    <div className="mt-4" data-testid="co2-equivalents">
      <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Equivale, ogni anno, a</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 p-3">
          <TreePine className="h-7 w-7 text-emerald-400 shrink-0" />
          <div>
            <p className="font-heading font-bold text-white text-lg leading-none">{fmtNum(trees)}</p>
            <p className="text-[11px] text-slate-400 mt-1">alberi che assorbono CO₂ per un anno</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-sky-500/[0.06] border border-sky-500/20 p-3">
          <Car className="h-7 w-7 text-sky-400 shrink-0" />
          <div>
            <p className="font-heading font-bold text-white text-lg leading-none">{fmtNum(km)}</p>
            <p className="text-[11px] text-slate-400 mt-1">km in auto non percorsi (emissioni evitate)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BandSplitBar = ({ rip, surplusLabel = "Eccedenza in rete" }) => {
  const segs = [
    { key: "f1_kwh", label: "F1 (giorno feriale)", color: "#D4A73A" },
    { key: "f2_kwh", label: "F2 (mattina/sera + sabato)", color: "#E7C878" },
    { key: "f3_kwh", label: "F3 coperta (dom/festivi + estate)", color: "#5AA9E6" },
    { key: "eccedenza_kwh", label: surplusLabel, color: "#5B6B84" },
  ].map((s) => ({ ...s, val: Math.max(0, Math.round(rip[s.key] || 0)) }));
  const total = segs.reduce((a, s) => a + s.val, 0) || 1;
  const visible = segs.filter((s) => s.val > 0);
  return (
    <div className="mt-5" data-testid="seu-band-split">
      <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Ripartizione dell'energia prodotta</p>
      <div className="flex h-4 w-full overflow-hidden rounded-full border border-white/10">
        {visible.map((s) => (
          <div key={s.key} title={`${s.label}: ${fmtNum(s.val)} kWh`}
            style={{ width: `${(s.val / total) * 100}%`, backgroundColor: s.color }} className="h-full" />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
        {visible.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-[11px]">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="text-slate-400 leading-tight">{s.label}</span>
            <span className="ml-auto font-mono-plex text-slate-200">{fmtNum(s.val)} kWh · {Math.round((s.val / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EoiCard = ({ icon: Icon, label, value, big }) => (
  <div className={`rounded-2xl p-5 ${big ? "bg-gold-500/10 border border-gold-500/30" : "bg-white/[0.03] border border-white/8"}`}>
    <Icon className={`h-5 w-5 mb-3 ${big ? "text-gold-300" : "text-slate-400"}`} />
    <p className="text-slate-400 text-xs">{label}</p>
    <p className={`font-heading font-black mt-1 ${big ? "text-gold-300 text-3xl" : "text-white text-2xl"}`}>{value}</p>
  </div>
);

const Stat = ({ label, value, good }) => (
  <div>
    <p className="text-slate-500 text-[11px]">{label}</p>
    <p className={`font-mono-plex text-sm ${good ? "text-gold-400" : "text-slate-200"}`}>{value}</p>
  </div>
);

const SectionHead = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2.5">
    <Icon className="h-4.5 w-4.5 text-gold-400" />
    <h3 className="font-heading font-bold text-lg text-white">{title}</h3>
  </div>
);

const Section = ({ icon, title, children }) => children ? (
  <div className="card-dark rounded-2xl p-6">
    <SectionHead icon={icon} title={title} />
    <p className="text-slate-300 text-sm leading-relaxed mt-3">{children}</p>
  </div>
) : null;

const ListSection = ({ icon, title, items, tone = "gold", ordered }) => {
  if (!items?.length) return null;
  const c = tone === "amber" ? "text-amber-400" : tone === "blue" ? "text-blue-400" : "text-gold-400";
  return (
    <div className="card-dark rounded-2xl p-6">
      <SectionHead icon={icon} title={title} />
      <ul className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-sm text-slate-300">
            <span className={`shrink-0 font-mono-plex text-xs mt-0.5 ${c}`}>{ordered ? `${i + 1}.` : "▹"}</span>
            <span className="leading-snug">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-slate-500">{children}</div>
);
